/**
 * Contribution Agent Widget — Floating bottom-right bubble → chat panel.
 * Legalia-themed version with page context tracking.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

function getPageContext(pathname) {
  if (pathname.startsWith('/contract-analysis')) return { page: 'Contract Analysis', section: null };
  if (pathname.startsWith('/law-analysis')) return { page: 'Law Analysis', section: null };
  if (pathname.startsWith('/policy-analysis')) return { page: 'Policy Analysis', section: null };
  if (pathname.startsWith('/legal-strategy')) return { page: 'Legal Strategy', section: null };
  if (pathname.startsWith('/patent-analysis')) return { page: 'Patent Analysis', section: null };
  if (pathname.startsWith('/compliance')) return { page: 'Compliance', section: null };
  if (pathname.startsWith('/legal-services')) return { page: 'Legal Services', section: null };
  if (pathname.startsWith('/tax-strategy')) return { page: 'Tax Strategy', section: null };
  if (pathname.startsWith('/qa')) return { page: 'Legal Q&A', section: null };
  if (pathname.startsWith('/contributions')) return { page: 'Contributions', section: null };
  if (pathname.startsWith('/admin')) return { page: 'Admin', section: null };
  if (pathname === '/') return { page: 'Home', section: null };
  return { page: 'Legalia', section: null };
}

export default function ContributionWidget() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [profile, setProfile] = useState(null);
  const messagesEndRef = useRef(null);

  const context = getPageContext(location.pathname);

  useEffect(() => {
    if (open && user && !profile) {
      fetch('/api/contributor/profile', { credentials: 'include' })
        .then(r => r.json())
        .then(d => d.data && setProfile(d.data))
        .catch(() => {});
    }
  }, [open, user]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (!user) return null;

  async function handleSend(e) {
    e?.preventDefault();
    const msg = input.trim();
    if (!msg || loading) return;

    setMessages(prev => [...prev, { role: 'user', text: msg }]);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch('/api/contributor/agent/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: msg, conversationId, context }),
      });
      const data = await res.json();
      if (data.success) {
        setConversationId(data.data.conversationId);
        setMessages(prev => [...prev, { role: 'assistant', ...data.data.response }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', text: 'Sorry, something went wrong. Try again.' }]);
    } finally {
      setLoading(false);
    }
  }

  async function handleAction(action) {
    if (action.type === 'navigate') {
      navigate(action.route);
      setOpen(false);
    } else if (action.type === 'create_proposal') {
      setLoading(true);
      try {
        const userMsgs = messages.filter(m => m.role === 'user');
        const lastMsg = userMsgs[userMsgs.length - 1];
        const description = lastMsg?.text || '';
        const title = `${action.proposalType === 'bug' ? 'Bug' : 'Report'} on ${context.page}: ${description.slice(0, 80)}`;

        const res = await fetch('/api/contributor/proposals', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ type: action.proposalType, title, description }),
        });
        const data = await res.json();
        if (data.success) {
          setMessages(prev => [...prev, {
            role: 'assistant',
            text: `Submitted! +5 pts earned. It's been sent to the committee for review.`,
          }]);
        }
      } catch {
        setMessages(prev => [...prev, { role: 'assistant', text: 'Failed to submit. Try again.' }]);
      } finally {
        setLoading(false);
      }
    }
  }

  function handleQuickAction(type) {
    const prompts = {
      bug: `I found a bug on the ${context.page} page:`,
      feature: `I have a suggestion for ${context.page}:`,
      compliance: `I have a compliance concern about ${context.page}:`,
    };
    setInput(prompts[type] || '');
  }

  const points = profile?.totalImpact || 0;

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-50 w-12 h-12 rounded-full bg-gradient-to-br from-amber-600 to-orange-600 text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center"
          title="Contribution Agent"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          {points > 0 && (
            <span className="absolute -top-1 -right-1 bg-amber-300 text-amber-900 text-[9px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
              {points > 999 ? `${Math.floor(points/1000)}k` : points}
            </span>
          )}
        </button>
      )}

      {/* Chat Panel */}
      {open && (
        <div className="fixed bottom-5 right-5 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-6rem)] flex flex-col rounded-2xl border border-gray-200 bg-white shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-600 to-orange-600 px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div>
              <h3 className="text-sm font-semibold text-white">Contribution Agent</h3>
              <p className="text-[10px] text-amber-200">
                {points} pts &middot; {context.page}
              </p>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/60 hover:text-white p-1">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Quick actions */}
          <div className="flex gap-1.5 px-3 py-2 border-b border-gray-100 flex-shrink-0">
            {[
              { key: 'bug', label: 'Bug', color: 'text-red-600 border-red-200 hover:bg-red-50' },
              { key: 'feature', label: 'Suggest', color: 'text-blue-600 border-blue-200 hover:bg-blue-50' },
              { key: 'compliance', label: 'Compliance', color: 'text-orange-600 border-orange-200 hover:bg-orange-50' },
            ].map(a => (
              <button key={a.key} onClick={() => handleQuickAction(a.key)}
                className={`px-2.5 py-1 text-[11px] font-medium bg-white border rounded-full transition-colors ${a.color}`}>
                {a.label}
              </button>
            ))}
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-3">
                  <svg className="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-gray-900">Hi {user.email?.split('@')[0]}!</p>
                <p className="text-xs text-gray-500 mt-1 max-w-[260px] mx-auto">Report bugs, suggest features, or flag compliance issues. Every contribution earns ownership in Legalia.</p>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-[13px] ${
                  msg.role === 'user' ? 'bg-amber-600 text-white rounded-br-md' : 'bg-gray-100 text-gray-800 rounded-bl-md'
                }`}>
                  <div className="whitespace-pre-wrap">{msg.text}</div>
                  {msg.actions && msg.actions.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {msg.actions.map((action, j) => (
                        <button key={j} onClick={() => handleAction(action)}
                          className="px-2.5 py-1 text-[11px] font-medium bg-white text-gray-700 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                          {action.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-2xl px-3.5 py-2">
                  <div className="flex gap-1">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={handleSend} className="px-3 py-2 border-t border-gray-100 flex gap-2 flex-shrink-0">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Report a bug, suggest a feature..."
              className="flex-1 px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
              disabled={loading}
            />
            <button type="submit" disabled={loading || !input.trim()}
              className="px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-xl hover:bg-amber-700 disabled:opacity-40 transition-colors">
              Send
            </button>
          </form>
        </div>
      )}
    </>
  );
}
